import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import Footer from "@/components/skinfill/Footer";

const Section = ({ n, title, children }) => (
  <section className="border-t border-[#EADECE] pt-8 mt-8 first:border-t-0 first:pt-0 first:mt-0">
    <h2 className="font-serif font-normal text-[#1C1417] text-xl sm:text-2xl tracking-tight">
      <span className="font-mono text-xs text-[#A31D4F] tracking-[0.25em] mr-3 align-middle">{n}.</span>
      {title}
    </h2>
    <div className="mt-4 space-y-3 text-[15px] font-light text-[#4a4144] leading-relaxed max-w-3xl">{children}</div>
  </section>
);

const Terms = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div data-testid="terms-page" className="bg-[#FAF7F5] min-h-screen">
      <header className="sticky top-0 z-50 bg-[#FAF7F5]/85 backdrop-blur-xl border-b border-[#EADECE]/60">
        <div className="max-w-5xl mx-auto px-5 sm:px-8 h-16 flex items-center justify-between">
          <Link to="/" data-testid="terms-logo-link" className="font-serif text-lg tracking-[0.08em] text-[#1C1417]">
            SKINFILL <span className="italic text-[#D84A75]">BACIO</span>
            <sup className="text-[9px] ml-0.5">®</sup>
          </Link>
          <Link
            to="/"
            data-testid="terms-back-link"
            className="inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.2em] text-[#A31D4F] hover:text-[#86143E] transition-colors"
          >
            <ArrowLeft size={14} />
            Back to site
          </Link>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-5 sm:px-8 py-16 sm:py-24">
        <p className="font-mono text-[11px] sm:text-xs uppercase tracking-[0.28em] text-[#A31D4F]">Legal</p>
        <h1 className="font-serif font-light text-[#1C1417] text-4xl sm:text-6xl tracking-tight mt-5 leading-[1.02]">
          Website Terms <span className="italic text-[#A31D4F]">&amp; Conditions</span>
        </h1>
        <p className="mt-4 font-mono text-[11px] uppercase tracking-[0.22em] text-[#6E6266]">
          Effective date: 27 September 2026
        </p>

        <div className="mt-12 space-y-2 text-[15px] font-light text-[#4a4144] leading-relaxed max-w-3xl">
          <p>Welcome to the Skinfill Bacio® Australia &amp; New Zealand website.</p>
          <p>
            This website is operated for the Australian and New Zealand markets by Dermalis Limited (New Zealand)
            and/or Dermalis Pty Ltd (Australia), collectively referred to as &ldquo;Dermalis&rdquo;,
            &ldquo;we&rdquo;, &ldquo;us&rdquo; or &ldquo;our&rdquo;.
          </p>
          <p>By accessing or using this website, you agree to these Terms &amp; Conditions.</p>
          <p>If you do not agree with these Terms, please do not use the website.</p>
        </div>

        <div className="mt-14">
          <Section n="1" title="Purpose of this website">
            <p>
              This website provides general information about Skinfill Bacio®, Dermalis and related professional
              services, education and distribution activities in Australia and New Zealand.
            </p>
            <p>
              Certain content may be intended specifically for appropriately qualified healthcare or aesthetic
              professionals.
            </p>
            <p>Information appearing on this website does not replace:</p>
            <ul className="list-disc pl-5 space-y-1.5 marker:text-[#D84A75]">
              <li>professional medical advice;</li>
              <li>diagnosis;</li>
              <li>consultation;</li>
              <li>individual patient assessment;</li>
              <li>informed consent;</li>
              <li>professional clinical judgement; or</li>
              <li>the manufacturer's authorised instructions for use.</li>
            </ul>
          </Section>

          <Section n="2" title="Not medical advice">
            <p>
              Nothing contained on this website constitutes personal medical advice or a recommendation that Skinfill
              Bacio® or any particular aesthetic treatment is suitable for an individual.
            </p>
            <p>
              Treatment suitability can only be determined following an appropriate consultation and assessment by a
              suitably qualified healthcare professional acting within the applicable professional and legal
              requirements of the jurisdiction in which treatment is provided.
            </p>
            <p>
              If you have questions concerning your health, a medical condition, medicines, pregnancy, allergies,
              contraindications or suitability for treatment, consult an appropriately qualified healthcare
              professional.
            </p>
          </Section>

          <Section n="3" title="Product information">
            <p>We take reasonable steps to ensure that information published on this website is accurate and current.</p>
            <p>
              However, product specifications, packaging, availability, indications, regulatory status and
              professional protocols may change.
            </p>
            <p>
              Healthcare professionals should refer to the current authorised product documentation, instructions for
              use, professional information and applicable regulatory requirements before using or recommending a
              product.
            </p>
            <p>
              In the event of any inconsistency between information on this website and current authorised product
              information, the current authorised product information takes precedence.
            </p>
          </Section>

          <Section n="4" title="Australia and New Zealand">
            <p>The website is intended primarily for visitors in Australia and New Zealand.</p>
            <p>
              Products, indications, professional requirements, availability and regulatory classifications may differ
              between countries.
            </p>
            <p>
              Information published for one jurisdiction should not automatically be interpreted as applying to
              another jurisdiction.
            </p>
          </Section>

          <Section n="5" title="Professional content">
            <p>
              Sections identified as being intended for healthcare or aesthetic professionals are provided for
              professional and educational purposes.
            </p>
            <p>
              By entering or using a professional section, you represent that you are appropriately entitled to access
              that information where such restrictions apply.
            </p>
            <p>Professional information does not override:</p>
            <ul className="list-disc pl-5 space-y-1.5 marker:text-[#D84A75]">
              <li>professional scope-of-practice requirements;</li>
              <li>applicable legislation;</li>
              <li>regulatory requirements;</li>
              <li>product instructions;</li>
              <li>professional standards; or</li>
              <li>individual clinical judgement.</li>
            </ul>
          </Section>

          <Section n="6" title="Before &amp; after images">
            <p>Any before-and-after images displayed on this website depict individual treatment outcomes.</p>
            <p>Individual results vary.</p>
            <p>
              Images are not intended to represent, promise or guarantee the result that another person will achieve.
            </p>
            <p>
              Results may depend on numerous factors including individual anatomy, age, skin and tissue condition,
              treatment technique, treatment plan, number of treatments, practitioner assessment and individual
              biological response.
            </p>
            <p>
              Where treatment details are provided with an image, they relate only to the individual case shown unless
              expressly stated otherwise.
            </p>
            <p>Images must not be interpreted as a substitute for professional consultation.</p>
          </Section>

          <Section n="7" title="Intellectual property">
            <p>Unless otherwise stated, all website content, including:</p>
            <ul className="list-disc pl-5 space-y-1.5 marker:text-[#D84A75]">
              <li>trademarks;</li>
              <li>trade names;</li>
              <li>logos;</li>
              <li>product imagery;</li>
              <li>photographs;</li>
              <li>video;</li>
              <li>graphics;</li>
              <li>text;</li>
              <li>layouts;</li>
              <li>designs; and</li>
              <li>other material</li>
            </ul>
            <p>
              is owned by Dermalis, the relevant manufacturer or brand owner, or is used under licence or with
              permission.
            </p>
            <p>
              SKINFILL BACIO®, related names, branding and intellectual property remain the property of their
              respective owners.
            </p>
            <p>You may view website materials for legitimate personal or professional informational purposes.</p>
            <p>
              You may not reproduce, modify, publish, distribute, sell, commercially exploit or use website materials
              without prior written authorisation from the relevant rights holder, except where permitted by law.
            </p>
          </Section>

          <Section n="8" title="Acceptable use">
            <p>You must not use this website:</p>
            <ul className="list-disc pl-5 space-y-1.5 marker:text-[#D84A75]">
              <li>for unlawful purposes;</li>
              <li>to infringe intellectual-property rights;</li>
              <li>to attempt unauthorised access to website systems;</li>
              <li>to introduce malicious code;</li>
              <li>to interfere with website operation or security;</li>
              <li>
                to scrape, systematically extract or reproduce content for commercial purposes without permission;
              </li>
              <li>to impersonate another individual or organisation;</li>
              <li>to submit false or misleading information.</li>
            </ul>
            <p>
              We reserve the right to restrict access where reasonably necessary to protect the website, Dermalis or
              other users.
            </p>
          </Section>

          <Section n="9" title="Third-party links">
            <p>
              This website may include links to third-party websites, social-media platforms, clinics, manufacturers or
              other external resources.
            </p>
            <p>Links are provided for convenience and do not necessarily constitute endorsement.</p>
            <p>
              Dermalis does not control third-party websites and is not responsible for their content, availability,
              security, privacy practices or terms.
            </p>
          </Section>

          <Section n="10" title="Enquiries and contact forms">
            <p>Submitting an enquiry through this website does not:</p>
            <ul className="list-disc pl-5 space-y-1.5 marker:text-[#D84A75]">
              <li>create a practitioner-patient relationship;</li>
              <li>constitute medical consultation;</li>
              <li>guarantee acceptance as a Dermalis customer or professional account;</li>
              <li>guarantee product availability;</li>
              <li>constitute an offer or acceptance of a commercial contract.</li>
            </ul>
            <p>
              Any commercial supply arrangement is subject to separate approval, eligibility requirements, account
              terms and applicable law.
            </p>
          </Section>

          <Section n="11" title="Website availability">
            <p>
              We aim to maintain the website in good working order but do not guarantee continuous or error-free
              access.
            </p>
            <p>The website may occasionally be unavailable due to:</p>
            <ul className="list-disc pl-5 space-y-1.5 marker:text-[#D84A75]">
              <li>maintenance;</li>
              <li>technical problems;</li>
              <li>updates;</li>
              <li>network interruptions;</li>
              <li>security events; or</li>
              <li>circumstances outside our reasonable control.</li>
            </ul>
          </Section>

          <Section n="12" title="Limitation of liability">
            <p>
              To the maximum extent permitted by law, Dermalis does not accept liability for loss arising solely from
              reliance on general website information without appropriate professional assessment or advice.
            </p>
            <p>
              Nothing in these Terms excludes, restricts or modifies any right, remedy, guarantee or liability that
              cannot lawfully be excluded or limited under applicable Australian or New Zealand law.
            </p>
          </Section>

          <Section n="13" title="No guarantee of results">
            <p>Aesthetic treatment outcomes vary between individuals.</p>
            <p>Neither Dermalis nor this website guarantees:</p>
            <ul className="list-disc pl-5 space-y-1.5 marker:text-[#D84A75]">
              <li>a particular cosmetic outcome;</li>
              <li>a particular degree of improvement;</li>
              <li>treatment suitability;</li>
              <li>duration of any result;</li>
              <li>absence of adverse effects; or</li>
              <li>that a treatment will meet an individual's expectations.</li>
            </ul>
            <p>
              Treatment decisions should be made with an appropriately qualified healthcare professional following
              individual assessment.
            </p>
          </Section>

          <Section n="14" title="Privacy">
            <p>
              Personal information collected through this website is handled in accordance with our{" "}
              <Link to="/privacy" className="text-[#A31D4F] hover:text-[#86143E] underline underline-offset-4 decoration-[#E893AE]/60">
                Privacy Policy
              </Link>
              .
            </p>
            <p>
              By submitting information through the website, you acknowledge that it will be handled in accordance
              with that policy.
            </p>
          </Section>

          <Section n="15" title="Changes to these Terms">
            <p>Dermalis may amend these Terms &amp; Conditions from time to time.</p>
            <p>Changes become effective when the updated Terms are published on this website.</p>
            <p>
              Continued use of the website following an update constitutes acceptance of the updated Terms to the
              extent permitted by law.
            </p>
          </Section>

          <Section n="16" title="Governing law">
            <p>For activities relating principally to New Zealand, these Terms are governed by the laws of New Zealand.</p>
            <p>
              For activities relating principally to Australia, these Terms are governed by applicable Australian law
              and, where relevant, the laws of the applicable Australian state or territory.
            </p>
            <p>
              Nothing in these Terms is intended to exclude mandatory consumer or other statutory protections
              applicable to a website user.
            </p>
          </Section>

          <Section n="17" title="Contact">
            <p>Questions regarding this website may be directed to:</p>
            <p>
              Dermalis
              <br />
              Official Skinfill Bacio® Distributor — Australia &amp; New Zealand
              <br />
              Email:{" "}
              <a href="mailto:info@dermalis.com.au" className="text-[#A31D4F] hover:text-[#86143E] underline underline-offset-4 decoration-[#E893AE]/60">
                info@dermalis.com.au
              </a>
            </p>
          </Section>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default Terms;
